import { useRef, useEffect, useState } from 'react';
import ScrollReveal from '@/components/shared/ScrollReveal';
import {
  ArrowRight,
  Hammer,
  Wrench,
  CircleDollarSign,
  ShieldCheck,
  Gift,
  PiggyBank,
  FileCheck2,
  Zap,
} from 'lucide-react';

const ENERGY_SIGNUP_URL =
  'https://green.igreenenergy.com.br/autoconexao/?id=30942&desc=10';

const benefits = [
  { icon: Hammer, title: 'Sem obras', description: 'Nenhuma alteração no imóvel.' },
  { icon: Wrench, title: 'Sem instalação', description: 'Não precisa instalar placas solares.' },
  { icon: CircleDollarSign, title: 'Sem investimento', description: 'Comece sem custo inicial.' },
  { icon: ShieldCheck, title: 'Sem fidelidade', description: 'Permaneça enquanto fizer sentido.' },
];

const highlights = [
  {
    icon: Gift,
    title: 'iGreen Club',
    description: 'Acesso gratuito a descontos em mais de 600 mil produtos e serviços.',
  },
  {
    icon: PiggyBank,
    title: 'Cashback Green',
    description:
      'Indique novos clientes, acumule cashback e aumente o seu desconto — podendo até mesmo zerar sua conta.',
  },
];

function BenefitCard({ item, index }: { item: typeof benefits[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group flex h-full flex-col items-center text-center sm:flex-row sm:items-start sm:text-left gap-3 p-3 md:p-4 rounded-xl bg-white border border-background-200/70 transition-all duration-500 hover:-translate-y-0.5 hover:shadow-md hover:border-primary-500/30 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-12 h-12 rounded-xl bg-primary-100/80 ring-1 ring-primary-200/50 flex items-center justify-center text-primary-600 shrink-0 transition-transform duration-300 group-hover:scale-110">
        <item.icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm md:text-base font-bold text-foreground-950 mb-0.5">{item.title}</h4>
        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

function HighlightCard({ item, index }: { item: typeof highlights[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left gap-3 p-3 md:p-4 rounded-xl bg-primary-50 border border-primary-200/60 transition-all duration-500 hover:border-primary-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ transitionDelay: `${(benefits.length + index) * 80}ms` }}
    >
      <div className="w-12 h-12 rounded-xl bg-primary-500 glow-green-sm flex items-center justify-center text-background-50 shrink-0 transition-transform duration-300 group-hover:scale-110">
        <item.icon className="w-6 h-6" />
      </div>
      <div className="min-w-0">
        <h4 className="text-sm md:text-base font-bold text-foreground-950 mb-0.5">{item.title}</h4>
        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
}

export default function EnergySection() {
  return (
    <section id="energia" className="immersive-panel bg-background-100">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-radial-green-sm rounded-full opacity-40 pointer-events-none" />

      <div className="split-viewport">
        <div className="contents">
          {/* Content */}
          <ScrollReveal className="split-viewport__content order-2 lg:order-1 text-center lg:text-left">
            <span className="inline-flex w-auto self-center lg:self-start items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 border border-primary-200 text-primary-700 text-sm font-semibold mb-5">
              <Zap className="w-4 h-4" />
              Energia
            </span>

            <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tighter leading-[1.1]">
              Economize na sua <br className="hidden md:inline" />conta de <span className="text-gradient">energia</span>
            </h2>

            <p className="mt-4 md:mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
              Sem investimento, sem instalação e sem fidelidade.
            </p>

            {/* Benefit cards */}
            <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {benefits.map((item, i) => (
                <BenefitCard key={item.title} item={item} index={i} />
              ))}
            </div>

            {/* Highlighted benefits */}
            <div className="mt-3 md:mt-4 space-y-3">
              {highlights.map((item, i) => (
                <HighlightCard key={item.title} item={item} index={i} />
              ))}
            </div>

            {/* CTA */}
            <div className="mt-6 md:mt-8">
              <a
                href={ENERGY_SIGNUP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary w-full sm:w-auto text-sm md:text-base px-5 md:px-8 py-3.5 md:py-4 group"
              >
                <FileCheck2 className="w-5 h-5" />
                Faça seu cadastro gratuitamente
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <p className="mt-3 text-xs text-foreground-500 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Benefícios sujeitos à elegibilidade e às condições vigentes. Cashback conforme as regras do programa.
              </p>
            </div>
          </ScrollReveal>

          {/* Image */}
          <ScrollReveal className="split-viewport__media order-1 lg:order-2" delay={120}>
            <div className="relative h-full">
              <div className="relative h-full overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20lifestyle%20photograph%20of%20a%20smiling%20Brazilian%20family%20of%20three%20sitting%20together%20at%20a%20wooden%20dining%20table%20in%20a%20bright%20modern%20home%2C%20reviewing%20a%20paper%20electricity%20bill%20with%20a%20laptop%20and%20calculator%20nearby%2C%20soft%20natural%20window%20light%2C%20clean%20minimalist%20interior%20with%20white%20walls%20and%20lush%20green%20plants%2C%20editorial%20photography%2C%20shallow%20depth%20of%20field%2C%20warm%20neutral%20tones%2C%20high%20detail&width=900&height=1200&seq=energia-lifestyle-01&orientation=portrait"
                  alt="Família revisando a conta de energia em casa para economizar com a iGreen Energy"
                  title="Economia na conta de energia com iGreen Energy"
                  className="h-full w-full object-cover object-center"
                  width="900"
                  height="1200"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
